/**
 * BatchOps.tsx — FR-015 / T316.
 *
 * The wired surface for the batch-operations cluster (specs/exploration/
 * stub_orphan_inventory.md §5 orphan #8):
 *
 *   - api.batchOperations.preview    (public-read query; what-if planner)
 *   - api.batchOperations.createJob  (requireAuth mutation; commits the job)
 *   - api.batchOperations.list       (read-only query; ongoing/completed jobs)
 *
 * The pattern is preview-first: the operator selects a target entity
 * type + ids + operation, the `preview` query (no side effects) returns
 * the proposed changes, and `createJob` commits them through
 * useMutationWithToast (FR-013 (H1) / FR-015 (O-D)).
 *
 * The mutation gate is `requireAuth` server-side (any logged-in operator
 * can create a batch job), so this screen does NOT need an AdminGate
 * wrapper — but every mutation IS still routed through the honest
 * wrapper, and argument KEYS only are logged (never values).
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@convex/api';
import {
  Btn,
  EmptyState,
  Field,
  Input,
  PageHead,
  Panel,
  Pill,
  Select,
  Skeleton,
  StatCard,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';

type EntityType = 'asset' | 'schedule' | 'channel';
type OperationType =
  | 'add_marker'
  | 'remove_marker'
  | 'mark_ready'
  | 'archive';

interface PreviewRow {
  entityId: string;
  entityName: string;
  currentState: Record<string, unknown>;
  proposedChanges: string;
}

interface BatchJobRow {
  _id: string;
  operationType: string;
  entityType: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  progress?: number;
  total?: number;
  createdAt?: number;
}

export function BatchOps() {
  const [entityType, setEntityType] = useState<EntityType>('asset');
  const [operationType, setOperationType] = useState<OperationType>('mark_ready');
  const [idsText, setIdsText] = useState('');

  const ids = useMemo(
    () =>
      idsText
        .split(/[\s,]+/)
        .map((s) => s.trim())
        .filter((s) => s.length > 0),
    [idsText],
  );

  // The preview query runs whenever the operator has selected at least
  // one entity id. Skip otherwise to avoid a useless round-trip.
  const preview = useQuery(
    api.batchOperations.preview,
    ids.length > 0
      ? {
          entityType,
          entityIds: ids,
          operationType,
        }
      : 'skip',
  ) as PreviewRow[] | undefined;

  const list = useQuery(api.batchOperations.list, { limit: 20 }) as
    | BatchJobRow[]
    | undefined;

  // The orphan-target mutation. Routed through the honest wrapper —
  // argument KEYS only flow to the log sink, never values.
  const createJobM = useMutationWithToast(api.batchOperations.createJob, {
    logLabel: 'batchOperations_createJob',
    successMessage: 'Trabajo en lote creado.',
    errorMessagePrefix: 'No se pudo crear el trabajo',
  });

  const handleCommit = async () => {
    if (ids.length === 0) return;
    try {
      await createJobM.run({
        entityType,
        entityIds: ids,
        operationType,
      });
      setIdsText('');
    } catch {
      // wrapper toasted; we just clear the input on success.
    }
  };

  return (
    <div>
      <PageHead
        title="Operaciones en lote"
        description="Aplica una operación a varios elementos a la vez. Previsualiza primero; ejecuta sólo cuando el resultado coincida con lo esperado."
      />

      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard
          label="Trabajos activos"
          value={(list ?? []).filter((j) => j.status === 'running').length}
          icon="activity"
        />
        <StatCard
          label="Pendientes"
          value={(list ?? []).filter((j) => j.status === 'pending').length}
          icon="clock"
        />
        <StatCard
          label="Completados"
          value={(list ?? []).filter((j) => j.status === 'completed').length}
          tone="good"
          icon="checkCircle"
        />
        <StatCard
          label="Fallidos"
          value={(list ?? []).filter((j) => j.status === 'failed').length}
          tone={(list ?? []).some((j) => j.status === 'failed') ? 'bad' : undefined}
          icon="alertTriangle"
        />
      </div>

      <Panel title="Nuevo trabajo" style={{ marginBottom: 24 }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 16,
            marginBottom: 16,
          }}
        >
          <Field label="Tipo de entidad">
            <Select
              value={entityType}
              onChange={(v: string) => setEntityType(v as EntityType)}
              full
              options={['asset', 'schedule', 'channel']}
            />
          </Field>
          <Field label="Operación">
            <Select
              value={operationType}
              onChange={(v: string) => setOperationType(v as OperationType)}
              full
              options={[
                'mark_ready',
                'archive',
                'add_marker',
                'remove_marker',
              ]}
            />
          </Field>
        </div>
        <Field
          label="IDs (uno por línea o separados por coma)"
          hint="Pega los identificadores internos de los elementos a procesar."
        >
          <Input
            value={idsText}
            onChange={setIdsText}
            placeholder="kg2... kg2... kg2..."
          />
        </Field>
        <div style={{ marginTop: 12, fontSize: 12, color: 'var(--muted-slate)' }}>
          {ids.length === 0
            ? 'Sin IDs para previsualizar.'
            : `${ids.length} entidad${ids.length === 1 ? '' : 'es'} en cola para previsualizar.`}
        </div>
      </Panel>

      <Panel
        title="Vista previa"
        right={
          ids.length > 0 && preview !== undefined ? (
            <Pill tone="neutral" size="sm">
              {preview.length} fila{preview.length === 1 ? '' : 's'}
            </Pill>
          ) : null
        }
        pad={0}
      >
        {ids.length === 0 ? (
          <EmptyState
            icon="eye"
            title="Sin previsualización"
            body="Pega al menos un ID arriba para ver la previsualización."
          />
        ) : preview === undefined ? (
          <div style={{ padding: 20 }}>
            <Skeleton h={40} />
          </div>
        ) : preview.length === 0 ? (
          <EmptyState
            icon="search"
            title="Sin coincidencias"
            body="Ninguno de los IDs proporcionados existe en la tabla."
          />
        ) : (
          <div>
            {preview.map((row, i) => (
              <div
                key={row.entityId}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.4fr 1fr 1.6fr',
                  gap: 12,
                  padding: '12px 20px',
                  borderBottom:
                    i < preview.length - 1
                      ? '1px solid var(--lightest-gray)'
                      : 'none',
                }}
              >
                <div>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>
                    {row.entityName}
                  </div>
                  <div
                    className="t-mono"
                    style={{ fontSize: 11, color: 'var(--muted-slate)' }}
                  >
                    {row.entityId}
                  </div>
                </div>
                <code
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    color: 'var(--body-muted)',
                  }}
                >
                  {JSON.stringify(row.currentState)}
                </code>
                <span style={{ fontSize: 13 }}>{row.proposedChanges}</span>
              </div>
            ))}
            <div
              style={{
                padding: '14px 20px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              <Btn variant="ghost" onClick={() => setIdsText('')}>
                Limpiar
              </Btn>
              <Btn
                variant="solid"
                icon="play"
                disabled={ids.length === 0 || createJobM.loading}
                onClick={() => void handleCommit()}
              >
                Ejecutar lote
              </Btn>
            </div>
          </div>
        )}
      </Panel>

      <Panel title="Trabajos recientes" pad={0} style={{ marginTop: 24 }}>
        {list === undefined ? (
          <div style={{ padding: 20 }}>
            <Skeleton h={40} />
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon="list"
            title="Sin trabajos"
            body="Aún no se ha ejecutado ningún trabajo en lote."
          />
        ) : (
          <div>
            {list.map((j, i) => (
              <div
                key={j._id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1.2fr 0.8fr 0.8fr 120px 120px',
                  gap: 12,
                  alignItems: 'center',
                  padding: '12px 20px',
                  borderBottom:
                    i < list.length - 1
                      ? '1px solid var(--lightest-gray)'
                      : 'none',
                }}
              >
                <span style={{ fontSize: 14 }}>{j.operationType}</span>
                <span
                  className="t-mono"
                  style={{ fontSize: 11, color: 'var(--muted-slate)' }}
                >
                  {j.entityType}
                </span>
                <Pill
                  tone={
                    j.status === 'completed'
                      ? 'good'
                      : j.status === 'failed'
                        ? 'bad'
                        : j.status === 'running'
                          ? 'live'
                          : 'neutral'
                  }
                  size="sm"
                >
                  {j.status}
                </Pill>
                <span
                  className="t-mono"
                  style={{ fontSize: 11, color: 'var(--muted-slate)' }}
                >
                  {j.progress ?? 0}/{j.total ?? 0}
                </span>
                <span
                  className="t-mono"
                  style={{ fontSize: 11, color: 'var(--muted-slate)' }}
                >
                  {j.createdAt
                    ? new Date(j.createdAt).toLocaleString('es-MX')
                    : '—'}
                </span>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

export default BatchOps;
